import { Analytics } from '@cobliteam/analytics';
import { type Rule, RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { TriggersFilters } from '@cobliteam/api-lib/src/dashboard/alerts/utils';
import type { Preferences } from '@cobliteam/common-types';
import { usePermissions } from '@cobliteam/permissions';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getAlertsRequester } from '../useEntity/useAlerts';
import useLocalStorage from '../useLocalStorage';
import { alertHookConfig } from './alertHookConfig';
import type { AlertHookParams, AlertHookType } from './types';
import useAlertHooksStore from './useAlertHooksStore';

// Matches the `useGetRules(RuleMode.FLEET_POLICY)` cache shape in `useEntity/useAlerts`, so a prior
// page fetch (e.g. /alerts) seeds this lookup; first-time callers pay the network round-trip.
const RULES_QUERY_KEY = ['ALERTS', 'RULES', RuleMode.FLEET_POLICY] as const;

// Fleet-wide dedup filter (see alertHookConfig): active fleet-policy triggers; the category match
// is applied client-side per hook type.
const DEDUP_FILTER: TriggersFilters = { page: 0, size: 200, active: true, ruleMode: RuleMode.FLEET_POLICY };

/**
 * Validates a journey-triggered alert suggestion and opens the hook modal when it should be shown.
 *
 * The caller (journey) decides *whether the hook is relevant* (e.g. only call CAMERA_OBSTRUCTED for
 * camera devices). This hook centralises the cross-cutting gates: user permission, the user's
 * global opt-out, prior dismissal for this context, rule availability for the fleet, and
 * fleet-wide dedup by category.
 *
 * Resolves to `true` when the modal opened, `false` when a gate blocked it — so journeys that defer
 * their own continuation (e.g. navigation) can resume immediately on `false` or via `onClosed`
 * when the modal eventually leaves the screen.
 *
 * Pass `force: true` for explicit user-initiated calls (button labelled "Create alert") — the
 * noise-reduction gates (opt-out, prior dismissal, fleet-wide dedup) are bypassed because the
 * user just asked for it. The permission gate and rule availability are still required: the
 * permission check is the API contract (the backend returns 403 without it) and without the rule
 * the modal can't resolve a condition to create the trigger.
 */
const useTriggerAlertHook = () => {
  const open = useAlertHooksStore((state) => state.open);
  const queryClient = useQueryClient();
  const { isAccessAllowed } = usePermissions();
  const hasAlertsPermission = isAccessAllowed('config-and-view:custom-alerts');

  // Preferences live in localStorage under `preferences` (same key the rest of the app uses).
  const [preferences] = useLocalStorage<Preferences>('preferences');
  const hideAlertHookSuggestions = preferences?.hideAlertHookSuggestions === true;

  const triggerAlertHook = useCallback(
    async <T extends AlertHookType>(type: T, params: AlertHookParams<T>, options?: { onClosed?: () => void; force?: boolean }): Promise<boolean> => {
      const force = options?.force === true;
      const config = alertHookConfig[type];

      // 1. The user lacks the alerts permission; the backend would 403 the rules fetch and any
      // subsequent create. Gate up-front so we neither call the API nor mount the modal.
      if (!hasAlertsPermission) return false;

      // 2. The user opted out of every alert hook suggestion globally.
      if (!force && hideAlertHookSuggestions) return false;

      // 3. The user already dismissed this specific hook context.
      if (!force && useAlertHooksStore.getState().dismissed[config.getDismissKey(params)]) return false;

      // 4. The rule must be available for this fleet. Fetched lazily through the shared react-query
      // cache so the network only fires the first time a journey actually triggers a hook (and is
      // a cache hit thereafter). `Array.isArray` also guards against an enveloped/error payload
      // reaching here — that must gate (return false), not throw.
      const rules = await queryClient
        .fetchQuery<Rule[]>({
          queryKey: [...RULES_QUERY_KEY],
          queryFn: () => getAlertsRequester().getRules(RuleMode.FLEET_POLICY),
        })
        .catch(() => undefined);
      if (!(Array.isArray(rules) && rules.some((rule) => rule.category === config.ruleCategory))) return false;

      // 5. Fleet-wide dedup: skip if an active trigger of this category already exists. The
      // triggers endpoint is paginated, so walk the pages until a match is found or the list ends.
      if (!force) {
        for (let page = 0, last = false; !last; page += 1) {
          const result = await getAlertsRequester()
            .getTriggers({ ...DEDUP_FILTER, page })
            .catch(() => undefined);
          if (!result) break; // fetch failed — fail open (no duplicate found) and show the suggestion.
          if (result.content.some((trigger) => trigger.active && trigger.condition?.rule.category === config.ruleCategory)) return false;
          last = result.last;
        }
      }

      // 6. All checks passed — open the suggestion and track the display.
      Analytics.track('Alert Hook Displayed', { hookType: type, dismissKey: config.getDismissKey(params), force });
      open(type, params, options?.onClosed);
      return true;
    },
    [hasAlertsPermission, hideAlertHookSuggestions, open, queryClient]
  );

  return { triggerAlertHook };
};

export default useTriggerAlertHook;
